import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Settings, Settings2, Palette, Layers, Users, Tag, Building2, MailCheck, Boxes, Receipt, Ruler, GitBranch,
} from 'lucide-react';
import MembersTab from './admin/MembersTab';
import ProfileTypesTab from './admin/ProfileTypesTab';
import ServicesTab from './admin/ServicesTab';
import StylesTab from './admin/StylesTab';
import MaterialsTab from './admin/MaterialsTab';
import OrganizationsTab from './admin/OrganizationsTab';
import InvitationsTab from './admin/InvitationsTab';
import ProjectTypesTab from './admin/ProjectTypesTab';
import PlanDefaultsTab from './admin/PlanDefaultsTab';
import FeatureFlagsTab from './admin/FeatureFlagsTab';
import AdminBillingPanel from '../components/admin/AdminBillingPanel';
import HUDPageHeader from '../components/HUDPageHeader';

const AdminPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('members');
  const [pendingCount, setPendingCount] = useState(0);

  if (!user?.is_admin) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Accès réservé aux administrateurs</p>
      </div>
    );
  }

  return (
    <div data-hud-page data-testid="admin-page" className="fade-in">
      <HUDPageHeader
        module="Admin"
        breadcrumb={[{ label: 'Hub', to: '/studio/dashboard' }, { label: 'Console admin' }]}
        icon={Settings}
        title="Administration"
        subtitle="Gérez les membres, services, styles et matériaux"
        actions={(
          <a
            href="/prompt-config"
            data-testid="admin-prompt-config-link"
            className="hud-btn hud-btn-secondary hud-btn-md"
          >
            <Settings2 className="w-4 h-4" />Config Prompt
          </a>
        )}
      />
      <div className="px-4 sm:px-6 lg:px-8 py-6">

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-gray-100 p-1 mb-6">
          <TabsTrigger value="members" data-testid="tab-members">
            <Users className="w-4 h-4 mr-2" />
            Membres
            {pendingCount > 0 && (
              <span className="ml-1.5 text-[10px] bg-amber-500 text-white rounded-full px-1.5 py-0.5 font-bold">{pendingCount}</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="profile-types" data-testid="tab-profile-types">
            <Tag className="w-4 h-4 mr-2" />
            Types de profil
          </TabsTrigger>
          <TabsTrigger value="services">
            <Settings className="w-4 h-4 mr-2" />
            Services
          </TabsTrigger>
          <TabsTrigger value="styles">
            <Palette className="w-4 h-4 mr-2" />
            Styles
          </TabsTrigger>
          <TabsTrigger value="materials">
            <Layers className="w-4 h-4 mr-2" />
            Matériaux
          </TabsTrigger>
          <TabsTrigger value="organizations" data-testid="tab-organizations">
            <Building2 className="w-4 h-4 mr-2" />
            Organisations
          </TabsTrigger>
          <TabsTrigger value="invitations" data-testid="tab-invitations">
            <MailCheck className="w-4 h-4 mr-2" />
            Invitations
          </TabsTrigger>
          <TabsTrigger value="project-types" data-testid="tab-project-types">
            <Boxes className="w-4 h-4 mr-2" />
            Types de projet
          </TabsTrigger>
          <TabsTrigger value="billing" data-testid="tab-billing">
            <Receipt className="w-4 h-4 mr-2" />
            Coûts
          </TabsTrigger>
          <TabsTrigger value="plan-defaults" data-testid="tab-plan-defaults">
            <Ruler className="w-4 h-4 mr-2" />
            Défauts plan
          </TabsTrigger>
          <TabsTrigger value="channels" data-testid="tab-channels">
            <GitBranch className="w-4 h-4 mr-2" />
            Canaux
          </TabsTrigger>
        </TabsList>

        <TabsContent value="members">
          <MembersTab onPendingCountChange={setPendingCount} />
        </TabsContent>
        <TabsContent value="profile-types">
          <ProfileTypesTab />
        </TabsContent>
        <TabsContent value="services">
          <ServicesTab />
        </TabsContent>
        <TabsContent value="styles">
          <StylesTab />
        </TabsContent>
        <TabsContent value="materials">
          <MaterialsTab />
        </TabsContent>
        <TabsContent value="organizations">
          <OrganizationsTab />
        </TabsContent>
        <TabsContent value="invitations">
          <InvitationsTab />
        </TabsContent>
        <TabsContent value="project-types">
          <ProjectTypesTab />
        </TabsContent>
        <TabsContent value="billing">
          <AdminBillingPanel />
        </TabsContent>
        <TabsContent value="plan-defaults">
          <PlanDefaultsTab />
        </TabsContent>
        <TabsContent value="channels">
          <FeatureFlagsTab />
        </TabsContent>
      </Tabs>
      </div>
    </div>
  );
};

export default AdminPage;
